import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

const LowStockTable = ({ products }) => {
    const { t } = useTranslation();
    if (!products || products.length === 0) return null;

    const maxShortage = Math.max(...products.map(p => Number(p.shortage) || 0));

    return (
        <div style={cardStyle}>
            <div style={headerStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ef4444' }} />
                    <div>
                        <span style={{ ...titleStyle, color: '#dc2626' }}>{t('common.analyticsWidgets.lowStock.title')}</span>
                        <div style={subtitleStyle}>{t('common.analyticsWidgets.lowStock.subtitle')}</div>
                    </div>
                </div>
                <span style={badgeStyle}>
                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 4 }} />
                    {products.length} critical
                </span>
            </div>
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid #fef2f2' }}>
                            <th style={thStyle('left')}>{t('common.analyticsColumns.product')}</th>
                            <th style={thStyle('left')}>{t('common.analyticsColumns.warehouse')}</th>
                            <th style={thStyle('right')}>{t('common.analyticsColumns.inStock')}</th>
                            <th style={thStyle('right')}>{t('common.analyticsColumns.pendingOrders')}</th>
                            <th style={thStyle('right')}>{t('common.analyticsColumns.shortage')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.map((row, i) => {
                            const shortage = Number(row.shortage) || 0;
                            const stock = Number(row.stock_quantity) || 0;
                            const pending = Number(row.pending_orders) || 0;
                            const pct = maxShortage > 0 ? (shortage / maxShortage) * 100 : 100;
                            const severity = pct > 70 ? '#ef4444' : pct > 40 ? '#f59e0b' : '#f97316';

                            return (
                                <tr key={row.product_size_id || i} style={{ borderBottom: '1px solid #fef2f2' }}
                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#fff7f7'}
                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                    <td style={{ ...tdStyle, maxWidth: 160 }}>
                                        <div style={{ fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {row.alias_name || row.product_name}
                                        </div>
                                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{row.category_name}</div>
                                    </td>
                                    <td style={tdStyle}>
                                        <span style={{
                                            fontSize: 11, fontWeight: 600, color: '#374151',
                                            backgroundColor: '#f3f4f6', borderRadius: 6,
                                            padding: '2px 8px', whiteSpace: 'nowrap'
                                        }}>
                                            {row.warehouse_name}
                                        </span>
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                                        <span style={{ fontWeight: 700, color: stock === 0 ? '#ef4444' : '#374151' }}>
                                            {Number(stock).toLocaleString('en-IN')}
                                        </span>
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: '#d97706' }}>
                                        {Number(pending).toLocaleString('en-IN')}
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                                        <div style={{ fontWeight: 800, color: severity, fontSize: 14 }}>
                                            {Number(shortage).toLocaleString('en-IN')}
                                        </div>
                                        <div style={{ height: 3, borderRadius: 2, backgroundColor: '#fee2e2', marginTop: 4, minWidth: 60 }}>
                                            <div style={{ height: '100%', borderRadius: 2, backgroundColor: severity, width: `${pct}%` }} />
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const cardStyle = {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    border: '1px solid #fecaca',
    boxShadow: '0 1px 4px rgba(220,38,38,0.06)',
    overflow: 'hidden',
    height: '100%',
};
const headerStyle = {
    padding: '18px 20px 14px',
    borderBottom: '1px solid #fef2f2',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#fff7f7',
};
const titleStyle = { fontSize: 15, fontWeight: 700, display: 'block' };
const subtitleStyle = { fontSize: 12, color: '#9ca3af', marginTop: 2 };
const badgeStyle = {
    fontSize: 11, fontWeight: 700, color: '#dc2626',
    backgroundColor: '#fee2e2', borderRadius: 20, padding: '3px 10px',
    whiteSpace: 'nowrap', flexShrink: 0,
};
const thStyle = (align) => ({
    padding: '10px 16px',
    textAlign: align,
    fontSize: 11,
    fontWeight: 700,
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    backgroundColor: '#fafafa',
    whiteSpace: 'nowrap',
});
const tdStyle = {
    padding: '10px 16px',
    verticalAlign: 'middle',
};

export default LowStockTable;
